import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Globe, Save, Upload, Image as ImageIcon, Type, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  { key: 'hero', label: 'Hero', fields: ['hero_faixa_etaria', 'hero_titulo_destaque', 'hero_subtitulo', 'hero_versiculo_ref', 'hero_descricao', 'hero_local', 'hero_dia', 'hero_horario'] },
  { key: 'sobre', label: 'Sobre', fields: ['sobre_texto1', 'sobre_texto2', 'sobre_lideres', 'sobre_pastora'] },
  { key: 'proposito', label: 'Propósito', fields: ['proposito_texto1', 'proposito_texto2', 'proposito_texto3', 'proposito_versiculo', 'proposito_versiculo_ref'] },
  { key: 'encontros', label: 'Encontros', fields: ['encontros_local_nome', 'encontros_local_sala', 'encontros_local_cidade', 'encontros_dia', 'encontros_horario', 'encontros_idade'] },
  { key: 'cta', label: 'Contato', fields: ['cta_titulo', 'cta_texto', 'cta_frase', 'cta_endereco', 'cta_horario'] },
];

const IMAGE_FIELDS = [
  { key: 'hero_img', label: 'Imagem Principal (Hero)' },
  { key: 'kids_img', label: 'Imagem das Crianças (Sobre)' },
  { key: 'prayer_img', label: 'Imagem de Oração' },
];

const EditarLandingPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState({});
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/landing-content`);
      setContent(res.data.content);
      setLabels(res.data.labels);
    } catch { toast.error('Erro ao carregar conteúdo'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/landing-content`, { content }, { headers: getAdminHeaders() });
      toast.success('Conteúdo salvo com sucesso!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (field, file) => {
    setUploading(prev => ({ ...prev, [field]: true }));
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/landing-content/upload-image?field=${field}`, formData, {
        headers: { ...getAdminHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setContent(prev => ({ ...prev, [field]: res.data.url }));
      toast.success('Imagem atualizada!');
    } catch { toast.error('Erro ao enviar imagem'); }
    finally { setUploading(prev => ({ ...prev, [field]: false })); }
  };

  const isLongField = (key) => key.includes('texto') || key.includes('descricao') || key.includes('versiculo') || key.includes('endereco') || key.includes('frase');

  if (loading) return <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center"><p>Carregando...</p></div>;

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-4xl mx-auto">
          <button data-testid="back-button" onClick={() => navigate(-1)} className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-7 h-7" strokeWidth={2.5} />
              <h1 className="font-nunito font-bold text-3xl">Editar Landing Page</h1>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => window.open('/', '_blank')} className="rounded-full text-xs border-white/30 text-white hover:bg-white/10">
                <Eye className="w-3.5 h-3.5 mr-1" /> Ver Página
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Save button */}
        <Button data-testid="save-landing" onClick={handleSave} disabled={saving} className="w-full mb-6 bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-xl py-5 text-base font-semibold gap-2">
          <Save className="w-5 h-5" /> {saving ? 'Salvando...' : 'Salvar Alterações'}
        </Button>

        <Tabs defaultValue="hero" className="space-y-4">
          <TabsList className="w-full bg-white border border-gray-200 rounded-xl p-1 flex flex-wrap gap-1">
            {SECTIONS.map(s => (
              <TabsTrigger key={s.key} value={s.key} className="flex-1 min-w-[80px] data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white rounded-lg text-xs font-semibold">
                {s.label}
              </TabsTrigger>
            ))}
            <TabsTrigger value="imagens" className="flex-1 min-w-[80px] data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white rounded-lg text-xs font-semibold">
              Imagens
            </TabsTrigger>
          </TabsList>

          {SECTIONS.map(section => (
            <TabsContent key={section.key} value={section.key}>
              <Card className="bg-white rounded-xl p-5 shadow-sm border border-[#E1ECE5] space-y-4">
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] flex items-center gap-2">
                  <Type className="w-5 h-5" /> {section.label}
                </h3>
                {section.fields.map(field => (
                  <div key={field}>
                    <label className="font-inter text-xs text-gray-500 font-semibold block mb-1">{labels[field] || field}</label>
                    {isLongField(field) ? (
                      <textarea
                        data-testid={`input-${field}`}
                        value={content[field] || ''}
                        onChange={(e) => setContent(prev => ({ ...prev, [field]: e.target.value }))}
                        className="w-full rounded-xl border border-gray-200 bg-gray-50/50 p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] resize-none"
                      />
                    ) : (
                      <Input
                        data-testid={`input-${field}`}
                        value={content[field] || ''}
                        onChange={(e) => setContent(prev => ({ ...prev, [field]: e.target.value }))}
                        className="rounded-xl border-gray-200 bg-gray-50/50"
                      />
                    )}
                  </div>
                ))}
              </Card>
            </TabsContent>
          ))}

          <TabsContent value="imagens">
            <Card className="bg-white rounded-xl p-5 shadow-sm border border-[#E1ECE5] space-y-6">
              <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] flex items-center gap-2">
                <ImageIcon className="w-5 h-5" /> Imagens
              </h3>
              {IMAGE_FIELDS.map(img => (
                <div key={img.key} className="border border-gray-100 rounded-xl p-4">
                  <label className="font-inter text-sm font-semibold text-[#1A4D2E] block mb-3">{img.label}</label>
                  {content[img.key] && (
                    <img src={content[img.key]} alt={img.label} className="w-full h-40 object-cover rounded-lg mb-3 border" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    ref={el => fileInputRefs.current[img.key] = el}
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) handleImageUpload(img.key, e.target.files[0]);
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={uploading[img.key]}
                    onClick={() => fileInputRefs.current[img.key]?.click()}
                    className="rounded-full text-xs gap-1"
                    data-testid={`upload-${img.key}`}
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {uploading[img.key] ? 'Enviando...' : 'Trocar Imagem'}
                  </Button>
                </div>
              ))}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <Navigation active="dashboard" />
    </div>
  );
};

export default EditarLandingPage;
